import {Input, Table, TablePaginationConfig} from 'antd';
import {SorterResult} from 'antd/es/table/interface';
import {useCallback, useMemo, useState} from 'react';

import {Spinner} from '../../../../layout/spinner/spinner';
import {Locale} from '../../../../provider/locale/locale';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {useSnackbar} from '../../../../provider/snackbar/snackbar-hook';
import {AnalyticsTarget, track} from '../../../../service/analytics/analytics';
import {useCompanyGroup} from '../../../../service/company-group/company-group-hook';
import {CompanyGroupType} from '../../../../service/company-group/company-group-type';
import {SEARCH_DEBOUNCE_WAIT_MS} from '../../../../service/debounce-hook/debouce-hook-const';
import {useDebouncedValue} from '../../../../service/debounce-hook/debounce-hook';
import {defaultPaginationState} from '../../../../util/hook';
import {getFromLocalStorage, saveToLocalStorage} from '../../../../util/local-storage';

import {CompaniesGroupTableAction} from './companies-group-table-action/companies-group-table-action';
import {
    companiesGroupColumnList,
    getOrderingValue,
    PAGE_SIZE_STORAGE_KEY,
    pageSizeSchema,
    resolveSearchQuery,
} from './companies-group-table-const';
import {CompanyGroupTableRowDataType} from './companies-group-table-type';
import * as styles from './companies-group-table.scss';

type PropsType = {
    className?: string;
};

const DEFAULT_PAGE_SIZE = defaultPaginationState.pageSize ?? 10;

function getInitialPagination(): TablePaginationConfig {
    return {
        ...defaultPaginationState,
        pageSize: getFromLocalStorage<number>(PAGE_SIZE_STORAGE_KEY, pageSizeSchema, DEFAULT_PAGE_SIZE),
    };
}

export function CompaniesGroupTable(props: PropsType): JSX.Element {
    const {className} = props;

    const {getLocalizedString} = useLocale();
    const [pagination, setPagination] = useState<TablePaginationConfig>(getInitialPagination);
    const [ordering, setOrdering] = useState<string | null>(null);
    const [searchInput, setSearchInput] = useState<string>('');

    const debouncedSearch = useDebouncedValue(searchInput.trim(), '', SEARCH_DEBOUNCE_WAIT_MS);
    const searchQuery = resolveSearchQuery(debouncedSearch);

    const {snackbar} = useSnackbar();
    const companyGroupHook = useCompanyGroup({
        count: Number(pagination.pageSize),
        page: Number(pagination.current),
        o: ordering,
        search: searchQuery,
    });

    const companyGroupHookList: Array<CompanyGroupType> = useMemo((): Array<CompanyGroupType> => {
        return companyGroupHook.result?.results || [];
    }, [companyGroupHook.result?.results]);

    const handleTableChange = useCallback(
        (
            newPagination: TablePaginationConfig,
            _filters: unknown,
            sorter: SorterResult<CompanyGroupTableRowDataType> | Array<SorterResult<CompanyGroupTableRowDataType>>
        ) => {
            const nextOrdering = getOrderingValue(sorter);
            const orderingChanged = nextOrdering !== ordering;

            if (orderingChanged) {
                setOrdering(nextOrdering);
            }

            if (newPagination.pageSize && newPagination.pageSize !== pagination.pageSize) {
                saveToLocalStorage(newPagination.pageSize, PAGE_SIZE_STORAGE_KEY);
            }

            // Reset to the first page whenever sorting changes, otherwise honour the requested page.
            setPagination({
                ...newPagination,
                current: orderingChanged ? 1 : newPagination.current,
            });

            if (pagination.current !== newPagination.current) {
                track(AnalyticsTarget.PersonalCabinet.Pagination, newPagination.current ?? 1);
            }
        },
        [ordering, pagination]
    );

    function handleSearchChange(value: string) {
        setSearchInput(value);
        setPagination((previous) => ({...previous, current: 1}));
    }

    const handleCompanyGroupRemove = useCallback(
        async (companyGroupId: number) => {
            const removingResult = await companyGroupHook.deleteCompanyGroup(companyGroupId);

            if (removingResult instanceof Error) {
                snackbar.error(<Locale stringKey="GROUP_OF_COMPANIES__DELETE__ERROR" />);
                return;
            }

            snackbar.success(<Locale stringKey="GROUP_OF_COMPANIES__DELETE__SUCCESSFULLY_DELETED" />);

            companyGroupHook.requestCompanyGroup();
        },
        [companyGroupHook, snackbar]
    );

    const convertCompanyTableRow = useCallback(
        (companyGroup: CompanyGroupType): CompanyGroupTableRowDataType => {
            const {companies, id, title} = companyGroup;

            return {
                name: title,
                count: companies.length,
                action: <CompaniesGroupTableAction companyGroup={companyGroup} onRemove={handleCompanyGroupRemove} />,
                key: String(id),
            };
        },
        [handleCompanyGroupRemove]
    );

    const dataList: Array<CompanyGroupTableRowDataType> = useMemo((): Array<CompanyGroupTableRowDataType> => {
        return companyGroupHookList.map(convertCompanyTableRow) || [];
    }, [companyGroupHookList, convertCompanyTableRow]);

    const tableLocale = searchQuery
        ? {emptyText: <Locale stringKey="GROUP_OF_COMPANIES__SEARCH__NOTHING_FOUND" />}
        : // eslint-disable-next-line no-undefined
          undefined;

    return (
        <div className={className}>
            <Spinner isShow={companyGroupHook.isInProgress} position="absolute" />

            <Input.Search
                allowClear
                className={styles.CompaniesGroupTable_search}
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder={getLocalizedString('GROUP_OF_COMPANIES__SEARCH__PLACEHOLDER')}
                value={searchInput}
            />

            <Table<CompanyGroupTableRowDataType>
                columns={companiesGroupColumnList}
                dataSource={dataList}
                locale={tableLocale}
                onChange={handleTableChange}
                pagination={{
                    size: 'default',
                    showSizeChanger: true,
                    hideOnSinglePage: false,
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: companyGroupHook.result?.count,
                }}
                scroll={{x: true}}
                size="middle"
            />
        </div>
    );
}
